import { useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { debounce } from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import { deleteBloster, fetchBlosterList, resetBlosterList } from '../../slices/bolster';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import { createActivityLog } from '../../slices/activity-log';
import logo from '../../Images/alux-logo.png';
import { font } from '../../Common/font';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Menu,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  message,
} from 'antd';
import { FileExcelOutlined, FilePdfOutlined, PlusOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ImportDataModal from '../../Common/ImportDataModal';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import formatFile from "../../Images/sample-files/bolster-format.xlsx";

// Audit columns visibility fix
function Bolster() {
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(100);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const [search, setSearch] = useState(localStorage.getItem('die_text_search_Bolster') || '');
  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'bloster_no',
    'bloster_image',
    'related_dies',
    'die_tool_count',
    'die_count',
    'press',
    'bolster_drawing',
    'pdf',
    'autocard',
    'created_at',
    'created_by',
    'updated_at',
    'updated_by',
    'action',
  ]);

  const ipAddress = useSelector(state => state.ip.ipAddress);
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector(state => state.auth);
  const { data = [], currentpage, totalRecord, totaltool, totaldie, status } = useSelector(state => state.bloster || {});

  useEffect(() => {
    dispatch(fetchAndSetIpAddress());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchBlosterList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [page, pagesize, search, orderBy, dispatch]);

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(deleteBloster(id))
      .unwrap()
      .then(() => {
        message.success('Bolster Archived Successfully!');
        dispatch(
          fetchBlosterList({
            page: currentpage,
            pagesize: 10,
            ...(search && { search: search }),
          }),
        );
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }
          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error);
        } else if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const dataSource = (Array.isArray(data) ? data : []).map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1,
  }));

  const navigate = useNavigate();
   const refreshTable = () => {
      dispatch(resetBlosterList());
      dispatch(
        fetchBlosterList({
          page: 1,
          pagesize,
          ...(search && { search }),
          ...(orderBy && { ordering: orderBy }),
        })
      );
    };
  const handleEdit = data => {
    navigate(`/edit-bolster/${data.id}`);
  };

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
    },
    {
      title: 'Bolster No.',
      dataIndex: 'bloster_no',
      key: 'bloster_no',
      sorter: true,
    },
    {
      title: 'Bolster Image',
      dataIndex: 'bloster_image',
      key: 'bloster_image',
      render: record => {
        const fileUrl = record;
        if (!fileUrl) return <span>N/A</span>;

        const fileExtension = fileUrl.split('.').pop().toLowerCase();

        if (fileExtension === 'pdf') {
          return (
            <Button
              type="link"
              style={{ padding: '0px' }}
              onClick={async () => {
                try {
                  const response = await fetch(fileUrl);
                  if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                  }
                  const blobData = await response.blob();
                  const blob = new Blob([blobData], { type: 'application/pdf' });
                  const blobURL = URL.createObjectURL(blob);
                  window.open(blobURL);
                } catch (error) {
                  console.error('Failed to fetch the PDF:', error);
                  message.error('Failed to open the PDF. Please try again.');
                }
              }}
            >
              View PDF
            </Button>
          );
        } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension)) {
          return (
            <div
              style={{
                width: '50px',
                height: '30px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '0',
              }}
            >
              <img
                src={fileUrl}
                alt="Die Diagram"
                style={{
                  maxWidth: '40px',
                  maxHeight: '38px',
                  objectFit: 'contain',
                }}
              />
            </div>
          );
        } else {
          return <span>Unsupported file type</span>;
        }
      },
    },
    {
      title: 'Suitable Dies',
      dataIndex: 'related_dies',
      key: 'related_dies',
      render: related_dies => {
        if (related_dies && related_dies.length > 0) {
          return related_dies.map(die => die.die_number).join(', ');
        }
        return <span>N/A</span>;
      },
    },
    {
      title: 'Press',
      dataIndex: 'press',
      key: 'press',
      render: value => {
        return value ? value.name : <span>N/A</span>;
      },
    },
    {
      title: 'Bolster Autocad',
      dataIndex: 'autocard',
      key: 'autocard',
      sorter: true,
      render: record => {
        const fileUrl = record;
        if (!fileUrl) return <span>N/A</span>;
        const fileExtension = fileUrl.split('.').pop().toLowerCase();
        if (fileExtension === 'pdf') {
          return (
            <Button
              type="link"
              style={{ padding: '0px' }}
              onClick={async () => {
                try {
                  const response = await fetch(fileUrl);
                  if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                  }
                  const blobData = await response.blob();
                  const blob = new Blob([blobData], { type: 'application/pdf' });
                  const blobURL = URL.createObjectURL(blob);
                  window.open(blobURL);
                } catch (error) {
                  console.error('Failed to fetch the PDF:', error);
                  message.error('Failed to open the PDF. Please try again.');
                }
              }}
            >
              View PDF
            </Button>
          );
        } else if (fileExtension === 'dwg') {
          return (
            <Button
              type="link"
              style={{ padding: '0px' }}
              onClick={() => {
                window.open(fileUrl, '_blank');
              }}
            >
              Download AutoCAD
            </Button>
          );
        } else {
          return <span>N/A</span>;
        }
      },
    },
    {
      title: 'Bolster PDF',
      dataIndex: 'pdf',
      key: 'pdf',
      render: record => {
        const fileUrl = record;
        if (!fileUrl) return <span>N/A</span>;
        const fileExtension = fileUrl.split('.').pop().toLowerCase();
        if (fileExtension === 'pdf') {
          return (
            <Button
              type="link"
              style={{ padding: '0px' }}
              onClick={async () => {
                const response = await fetch(fileUrl);
                if (!response.ok) {
                  throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const blobData = await response.blob();
                const blob = new Blob([blobData], { type: 'application/pdf' });
                const blobURL = URL.createObjectURL(blob);
                window.open(blobURL);
              }}
            >
              View PDF
            </Button>
          );
        } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension)) {
          return <img width="55" height="55" src={fileUrl} alt="Die Diagram" />;
        } else {
          return <span>Unsupported file type</span>;
        }
      },
    },
    {
      title: 'Number Of Profile',
      dataIndex: 'die_count',
      key: 'die_count',
      sorter: true,
      width: 300,
    },
    {
      title: 'Number Of tools',
      dataIndex: 'die_tool_count',
      key: 'die_tool_count',
      sorter: true,
      width: 300,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record && typeof record === 'object'
              ? `${record.first_name || ''} ${record.last_name || ''}`.trim() || 'N/A'
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: record => {
        return (
          <span>
            {record
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record && typeof record === 'object'
              ? `${record.first_name || ''} ${record.last_name || ''}`.trim() || 'N/A'
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          {currentUser.permissions.permissions.bloster.includes(
            'change_blostermaster',
          ) && (
              <Tooltip placement="top" title={'Edit'}>
                <span onClick={() => handleEdit(record)}>
                  <EditIcon style={{ cursor: 'pointer' }} />
                </span>
              </Tooltip>
            )}
          {currentUser.permissions.permissions.bloster.includes(
            'delete_blostermaster',
          ) && (
              <Link>
                <Tooltip title="Archive">
                  <Popconfirm
                    title="Are you sure you want to archive this record?"
                    onConfirm={() => handleDelete(record.id)}
                    onCancel={() => message.info('Action cancelled')}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Archive style={{ cursor: 'pointer' }} />
                  </Popconfirm>
                </Tooltip>
              </Link>
            )}
        </Space>
      ),
    },
  ];

  const filteredColumns = columns.filter(column => checkedList.includes(column.key));

  const handleSearch = e => {
    const value = e.target.value;
    debouncedAPICall(value);
    setSearch(value);
    localStorage.setItem('die_text_search_Bolster', value);
  };

  const debouncedAPICall = useMemo(
    () =>
      debounce(query => {
        if (query.length >= 3) {
          dispatch(fetchBlosterList({ search: query }));
          localStorage.setItem('die_text_search_Bolster', query);
        }
      }, 50),
    [dispatch],
  );

  useEffect(() => {
    return () => {
      debouncedAPICall.cancel();
    };
  }, [debouncedAPICall]);

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const resetFilter = () => {
    setSearch('');
    setOrderBy(null);
    localStorage.removeItem('die_text_search_Bolster');
    localStorage.removeItem('die_group_search_Bolster');
    dispatch(fetchBlosterList({ page: 1, pagesize }));
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';
    if (sortField === 'bloster_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'bloster_no';
    }
    if (sortField === 'bloster_no' && sortOrder === 'descend') {
      sortingEnumKey = '-bloster_no';
    }
    if (sortField === 'related_dies' && sortOrder === 'ascend') {
      sortingEnumKey = 'related_dies';
    }
    if (sortField === 'related_dies' && sortOrder === 'descend') {
      sortingEnumKey = '-related_dies';
    }
    if (sortField === 'bloster_image' && sortOrder === 'ascend') {
      sortingEnumKey = 'bloster_image';
    }
    if (sortField === 'bloster_image' && sortOrder === 'descend') {
      sortingEnumKey = '-bloster_image';
    }
    if (sortField === 'press' && sortOrder === 'ascend') {
      sortingEnumKey = 'press';
    }
    if (sortField === 'press' && sortOrder === 'descend') {
      sortingEnumKey = '-press';
    }
    if (sortField === 'autocard' && sortOrder === 'ascend') {
      sortingEnumKey = 'autocard';
    }
    if (sortField === 'autocard' && sortOrder === 'descend') {
      sortingEnumKey = '-autocard';
    }
    if (sortField === 'pdf' && sortOrder === 'ascend') {
      sortingEnumKey = 'pdf';
    }
    if (sortField === 'pdf' && sortOrder === 'descend') {
      sortingEnumKey = '-pdf';
    }
    if (sortField === 'die_count' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_count';
    }
    if (sortField === 'die_count' && sortOrder === 'descend') {
      sortingEnumKey = '-die_count';
    }
    if (sortField === 'die_tool_count' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_tool_count';
    }
    if (sortField === 'die_tool_count' && sortOrder === 'descend') {
      sortingEnumKey = '-die_tool_count';
    }
    if (sortField === 'created_by__first_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'created_by__first_name';
    }
    if (sortField === 'created_by__first_name' && sortOrder === 'descend') {
      sortingEnumKey = '-created_by__first_name';
    }
    if (sortField === 'created_by__last_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'created_by__last_name';
    }
    if (sortField === 'created_by__last_name' && sortOrder === 'descend') {
      sortingEnumKey = '-created_by__last_name';
    }
    if (sortField === 'updated_by__first_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_by__first_name';
    }
    if (sortField === 'updated_by__first_name' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_by__first_name';
    }
    if (sortField === 'updated_by__last_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_by__last_name';
    }
    if (sortField === 'updated_by__last_name' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_by__last_name';
    }
    return sortingEnumKey;
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  };

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row>
            <Col span={12}>
              <Checkbox value="SrNo" disabled>
                Sr. No.
              </Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="bloster_no">Bolster No.</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="bloster_image">Bolster Image</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="press">Press</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="autocard">Bolster Autocad</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="pdf">Bolster PDF</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="die_tool_count">Number Of Tools</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="die_count">Number Of Profiles</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  const allCsvColumns = [
    { header: 'Sr. No.', dataKey: 'SrNo', field: 'SrNo' },
    { header: 'Bolster No.', dataKey: 'bloster_no', field: 'bloster_no' },
    { header: 'Press', dataKey: 'press', field: 'press' },
    {
      header: 'Number Of Tools',
      dataKey: 'die_tool_count',
      field: 'die_tool_count',
    },
    { header: 'Number Of Profiles', dataKey: 'die_count', field: 'die_count' },
    { header: 'Created At', dataKey: 'createdAt', field: 'created_at' },
    { header: 'Created By', dataKey: 'createdBy', field: 'created_by' },
    { header: 'Updated At', dataKey: 'updatedAt', field: 'updated_at' },
    { header: 'Updated By', dataKey: 'updatedBy', field: 'updated_by' },
  ];

  const csvHeaders = allCsvColumns
    .filter(col => checkedList.includes(col.field))
    .map(col => ({
      label: col.header,
      key: col.dataKey,
    }));

  const csvListData = dataSource.map((item, index) => {
    const row = {
      SrNo: index + 1,
    };
    allCsvColumns
      .filter(col => checkedList.includes(col.field))
      .forEach(col => {
        switch (col.field) {
          case 'SrNo':
            row.SrNo = item.SrNo || 'N/A';
            break;
          case 'bloster_no':
            row.bloster_no = item?.bloster_no || 'N/A';
            break;
          case 'press':
            row.press = item?.press?.name || 'N/A';
            break;
          case 'die_tool_count':
            row.die_tool_count = item?.die_tool_count || '0';
            break;
          case 'die_count':
            row.die_count = item?.die_count || '0';
            break;
          case 'created_at':
            row.createdAt = item.created_at
              ? moment(item.created_at).format('DD-MM-YYYY')
              : 'N/A';
            break;
          case 'created_by':
            row.createdBy =
              item.created_by && typeof item.created_by === 'object'
                ? `${item.created_by.first_name || ''} ${
                    item.created_by.last_name || ''
                  }`.trim() || 'N/A'
                : 'N/A';
            break;
          case 'updated_at':
            row.updatedAt = item.updated_at
              ? moment(item.updated_at).format('DD-MM-YYYY HH:mm')
              : 'N/A';
            break;
          case 'updated_by':
            row.updatedBy =
              item.updated_by && typeof item.updated_by === 'object'
                ? `${item.updated_by.first_name || ''} ${
                    item.updated_by.last_name || ''
                  }`.trim() || 'N/A'
                : 'N/A';
            break;
          default:
            row[col.dataKey] = 'N/A';
        }
      });
    return row;
  });

  const handlePdfDownload = async () => {
    try {
      const pdf = new jsPDF("l", "pt", "a4");
      pdf.addFileToVFS("WorkSans-normal.ttf", font);
      pdf.addFont("WorkSans-normal.ttf", "WorkSans", "normal");
      pdf.setFont("WorkSans");

      const pdfColumns = [
        { header: "Sr. No.", dataKey: "SrNo", field: "SrNo" },
        { header: "Bolster No.", dataKey: "bloster_no", field: "bloster_no" },
        { header: "Suitable Dies", dataKey: "related_dies", field: "related_dies" },
        { header: "Press", dataKey: "press", field: "press" },
        { header: "Bolster Autocad", dataKey: "autocard", field: "autocard" },
        { header: "Bolster PDF", dataKey: "pdf", field: "pdf" },
        { header: "Number Of Tools", dataKey: "die_tool_count", field: "die_tool_count" },
        { header: "Number Of Profiles", dataKey: "die_count", field: "die_count" },
        { header: "Created At", dataKey: "createdAt", field: "created_at" },
        { header: "Created By", dataKey: "createdBy", field: "created_by" },
        { header: "Updated At", dataKey: "updatedAt", field: "updated_at" },
        { header: "Updated By", dataKey: "updatedBy", field: "updated_by" },
      ];

      const selectedColumns = pdfColumns.filter(col => checkedList.includes(col.field));

      const rows = dataSource.map((item, rowIndex) => {
        const row = {};
        selectedColumns.forEach(col => {
          switch (col.field) {
            case "SrNo":
              row.SrNo = item.SrNo || "N/A";
              break;
            case "bloster_no":
              row.bloster_no = item?.bloster_no || "N/A";
              break;
            case "related_dies":
              row.related_dies =
                item.related_dies && item.related_dies.length > 0
                  ? item.related_dies.map(die => die.die_number).join(", ")
                  : "N/A";
              break;
            case "press":
              row.press = item?.press?.name || "N/A";
              break;
            case "autocard":
              row.autocard = item?.autocard ? item.autocard.split("/").pop() : "N/A";
              break;
            case "pdf":
              row.pdf = item?.pdf ? item.pdf.split("/").pop() : "N/A";
              break;
            case "die_tool_count":
              row.die_tool_count = item?.die_tool_count || "0";
              break;
            case "die_count":
              row.die_count = item?.die_count || "0";
              break;
            case "created_at":
              row.createdAt = item.created_at
                ? moment(item.created_at).format("DD-MM-YYYY HH:mm")
                : "N/A";
              break;
            case "created_by":
              row.createdBy =
                item.created_by && typeof item.created_by === "object"
                  ? `${item.created_by.first_name || ""} ${
                      item.created_by.last_name || ""
                    }`.trim() || "N/A"
                  : "N/A";
              break;
            case "updated_at":
              row.updatedAt = item.updated_at
                ? moment(item.updated_at).format("DD-MM-YYYY HH:mm")
                : "N/A";
              break;
            case "updated_by":
              row.updatedBy =
                item.updated_by && typeof item.updated_by === "object"
                  ? `${item.updated_by.first_name || ""} ${
                      item.updated_by.last_name || ""
                    }`.trim() || "N/A"
                  : "N/A";
              break;
            default:
              row[col.dataKey] = item[col.field] || "N/A";
          }
        });
        return row;
      });

      pdf.setFont("WorkSans", "bold");
      pdf.setFontSize(16);
      pdf.text("Bolsters", pdf.internal.pageSize.width / 2, 40, { align: "center" });

      pdf.addImage(logo, "PNG", pdf.internal.pageSize.width - 100, 10, 90, 30);

      pdf.autoTable({
        columns: selectedColumns.map(col => ({ header: col.header, dataKey: col.dataKey })),
        body: rows,
        startY: 80,
        theme: "grid",
        styles: {
          font: "WorkSans",
          halign: "left",
          cellPadding: 5,
          textColor: [0, 0, 0],
          fontSize: 10,
          overflow: "linebreak",
        },
        headStyles: {
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fillColor: [240, 240, 240],
        },
        margin: { left: 10, right: 10, bottom: 40 },
        columnStyles: {
          related_dies: { cellWidth: 150 },
          autocard: { cellWidth: 100 },
          pdf: { cellWidth: 100 },
        },
        didDrawPage: (data) => {
          const pageCount = pdf.internal.getNumberOfPages();
          const pageSize = pdf.internal.pageSize;
          const pageHeight = pageSize.height;

          const dateStr = moment().format("DD-MM-YYYY HH:mm");
          pdf.setFontSize(9);
          pdf.text(dateStr, 40, pageHeight - 20);

          pdf.text(`Page ${data.pageNumber} of ${pageCount}`, pageSize.width - 100, pageHeight - 20);
        },
      });

      pdf.save(`bolster_${+new Date()}.pdf`);

      dispatch(
        createActivityLog({
          action: "DOWNLOAD",
          module_name: "Bolster",
          ip_address: ipAddress,
          discription: "Bolster pdf download successfully!",
        })
      );
    } catch (error) {
      console.error("PDF generation error:", error);
      message.error("Failed to generate PDF. Please try again later.");
    }
  };

  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Bolster',
        ip_address: ipAddress,
        discription: 'Bolster excel download successfully!',
      }),
    );
  };

  return (
    <div className="mainwrap">
      <div className="sticyaction">
        <Row
          className="pagenamerow mb-0"
          justify="space-between"
          align="middle"
        >
          <Col>
            <h2 style={{ margin: 0 }}>Bolster</h2>
            <div className="bredcrumbwrp">
              <Link to="/die-profilelist" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  //{ title: <Link to="/die-profilelist">Profiles</Link> },
                   { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Bolsters' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                onChange={handleSearch}
                style={{ width: 200 }}
                value={search}
              />
              <Tooltip placement="top" title="Reset Filter">
                <Button
                  type="primary"
                  disabled={!search && !orderBy}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
              {currentUser.permissions.permissions.bloster.includes(
                'download_bolster_excel_copy',
              ) && (
                  <Tooltip placement="top" title={'Export Excel'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      style={{ width: '10px' }}
                      onClick={activityLog}
                    >
                      <CSVLink
                        filename={'Bolsters.csv'}
                        className="iconlink"
                        style={{ width: '10px' }}
                        data={csvListData}
                        headers={csvHeaders}
                      >
                        <FileExcelOutlined />
                      </CSVLink>
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.bloster.includes(
                'download_bolster_pdf_copy',
              ) && (
                  <Tooltip placement="top" title={'Export PDF'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      onClick={handlePdfDownload}
                    >
                      <FilePdfOutlined />
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.bloster.includes(
                'add_blostermaster',
              ) && (
                  <Link to="/add-bolster">
                    <Button type="primary" icon={<PlusOutlined />}>
                      <Tooltip title="Add Bolster">Add Bolster</Tooltip>
                    </Button>
                  </Link>
                )}
                      {/* <Button type="primary" icon={<UploadOutlined  />} onClick={() => setOpenModal(true)}>
                                Import Data
                              </Button> */}
              {currentUser.permissions.permissions.bloster.includes(
                'delete_blostermaster',
              ) && (
                  <Tooltip placement="top" title="Archive">
                    <Link to="/archive-bolster" className="iconlink">
                      <Archiveicon style={{ cursor: 'pointer' }} />
                    </Link>
                  </Tooltip>
                )}
              <Tooltip placement="topLeft" title="Column Filter Options">
                <Dropdown
                  overlay={columnMenu}
                  trigger={['click']}
                  placement="bottomRight"
                  visible={dropdownVisible}
                  onVisibleChange={setDropdownVisible}
                >
                  <Button
                    type="primary"
                    icon={<ColumnList />}
                    onClick={e => e.preventDefault()}
                  />
                </Dropdown>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </div>
      <ImportDataModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        refreshTable={refreshTable}
        formatFilePath={formatFile}
        model={'bolster'}
      />
      <div className="tablewrp">
        <Table
          rowKey="id"
          dataSource={dataSource}
          columns={filteredColumns}
          pagination={false}
          loading={status === 'loading'}
          onChange={handleTableChange}
          scroll={{ x: '1000px' }}
        />
      </div>

      <Pagination
        current={page}
        pageSize={pagesize}
        total={totalRecord}
        showSizeChanger
        onShowSizeChange={handlePageSizeChange}
        pageSizeOptions={['20', '50', '100']}
        showTotal={total => (
          <Row gutter={16}>
            <Col>
              <span
                style={{
                  backgroundColor: 'rgb(211, 236, 248)',
                  padding: '3px 10px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  display: 'inline-block',
                }}
              >
                Total Profiles: {totaltool} items
              </span>
            </Col>
            <Col>
              <span
                style={{
                  backgroundColor: '#ffeaea',
                  padding: '3px 10px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  display: 'inline-block',
                }}
              >
                Total No Of Tools: {totaldie} items
              </span>
            </Col>
            <Col>
              <span>Total: {total} items</span>
            </Col>
          </Row>
        )}
        onChange={handlePageChange}
        locale={{ items_per_page: 'Items / Page' }}
      />
    </div>
  );
}

export default Bolster;